import { useEffect } from 'react';
import { useSafeState } from '../use-safe-state';
import {
  DEFAULT_LOCALIZED_IMAGES_SIZES,
  DEFAULT_LOCALIZED_IMAGES_FORMATS,
  DEFAULT_LOCALIZED_IMAGES_RESOLUTIONS,
  DEFAULT_LOCALIZED_IMAGES_DIRECTORY,
  DEFAULT_LOCALIZED_IMAGES_SIZES_ORDER,
  DEFAULT_LOCALIZED_IMAGES_FORMATS_ORDER
} from './constants';
import {
  getImageLocale,
  getImageLocaleDirectory,
  sortImagesBySizeAndFormat
} from './helpers';

/**
 * This hook accepts a 'key' and additional optional params and returns an array of localized image assets.
 * For each image asset a callback is called to load the asset.
 * @param {string} key - string representing collection of image files e.g. 'Strava.com-Home-Screen1'
 * @param {string} locale - locale to use in filename / filepath e.g. 'de-DE' (optional, if none provided, defaults to 'defaultDirectory')
 * @param {Array} sizes - sizes to use for a given asset. e.g. [{ id: 'lg', value: 'large', minVw: '1200px' }, { id: 'md', value: 'medium', minVw: '992px' }]
 * @param {Array} formats - formats to use for a given asset. e.g. ['webp', 'png']
 * @param {Array} resolutions - resolutions to use for a given asset e.g. ['1x', '2x']
 * @param {string} defaultDirectory - directory to use if no locale provided. Defaults to 'shared'
 * @param {Function} imageImportCallback - Callback function; called for each image instance. Params passed: imageLocaleDirectory, imageKey, imageSize, imageFormat, imageResolution, imageLocale
 * @param {Function} logError - Error handling function
 * @returns {Array} images
 */

export const useLocalizedImages = ({
  key,
  locale,
  sizes = DEFAULT_LOCALIZED_IMAGES_SIZES,
  formats = DEFAULT_LOCALIZED_IMAGES_FORMATS,
  resolutions = DEFAULT_LOCALIZED_IMAGES_RESOLUTIONS,
  defaultDirectory = DEFAULT_LOCALIZED_IMAGES_DIRECTORY,
  imageImportCallback = () => {},
  logError
}) => {
  const [images, setImages] = useSafeState([]);

  useEffect(() => {
    // Import asset from dynamic filepath
    const importImage = async (imageData) => {
      try {
        return imageImportCallback(imageData);
      } catch (error) {
        logError(new Error(`Error in imageImportCallback, Error: ${error}`));
        return null;
      }
    };

    const initImages = async () => {
      sizes.forEach((size) => {
        formats.forEach((format) => {
          resolutions.forEach(async (resolution) => {
            // Import file
            const file = await importImage({
              imageLocaleDirectory: getImageLocaleDirectory(
                locale,
                defaultDirectory
              ),
              imageKey: key,
              imageSize: size,
              imageFormat: format,
              imageResolution: resolution,
              imageLocale: getImageLocale(locale)
            });

            if (file) {
              // Update state with new image
              setImages((prevImages) => [
                ...prevImages,
                { size, format, resolution, file }
              ]);
            }
          });
        });
      });
    };

    initImages();
  }, [
    key,
    locale,
    formats,
    sizes,
    resolutions,
    imageImportCallback,
    logError,
    defaultDirectory,
    setImages
  ]);

  // Note: Sorting the images before returning as the order is not guranteed due to async import operations.
  // Order is important for use cases such as <source> tags within <picture> elements
  return images.sort(
    sortImagesBySizeAndFormat(
      DEFAULT_LOCALIZED_IMAGES_SIZES_ORDER,
      DEFAULT_LOCALIZED_IMAGES_FORMATS_ORDER
    )
  );
};

export default useLocalizedImages;
